import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { socialColors } from '@shared/config';
import { cn } from '@shared/lib';
import { getInstallPlatform } from '@shared/pwa/install';
import { Button, CheckIcon, DzenIcon, Modal, TelegramIcon, VkIcon } from '@shared/ui';
import type { SocialRewardController } from '../model/useSocialReward';
import './SocialReward.css';

type Network = 'telegram' | 'vk' | 'dzen';
const icons = { telegram: TelegramIcon, vk: VkIcon, dzen: DzenIcon };

/** tg:// opens the installed app in place; https links go to a new tab. */
function LinkAction({
  href,
  children,
  className,
  onClick,
}: {
  href: string;
  children: ReactNode;
  className: string;
  onClick?: () => void;
}) {
  const external = href.startsWith('https:');
  return (
    <a
      className={className}
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      onClick={onClick}
    >
      {children}
    </a>
  );
}

/** One line per network: icon, name + short status, a single action on the right, optional fine print below. */
function NetworkRow({
  network,
  verified = false,
  status,
  action,
  extra,
  quiet = false,
}: {
  network: Network;
  verified?: boolean;
  status: string;
  action?: ReactNode;
  extra?: ReactNode;
  quiet?: boolean;
}) {
  const { t } = useTranslation();
  const Icon = icons[network];
  const name = t(`community.${network}.name`);
  return (
    <section className={cn('sr-row', quiet && 'sr-row--quiet')} aria-label={name}>
      <span
        className={cn('sr-row__icon', network === 'dzen' && 'sr-row__icon--dzen')}
        style={
          network === 'dzen'
            ? undefined
            : { color: socialColors[network], backgroundColor: `${socialColors[network]}1f` }
        }
      >
        <Icon aria-hidden="true" />
      </span>
      <div className="sr-row__body">
        <h3>{name}</h3>
        <p className={cn('sr-row__status', verified && 'sr-row__status--ok')}>
          {verified ? t('socialReward.verified') : status}
        </p>
      </div>
      <div className="sr-row__action">
        {verified ? <CheckIcon className="sr-verified-icon" aria-hidden="true" /> : action}
      </div>
      {!verified && extra && <div className="sr-row__extra">{extra}</div>}
    </section>
  );
}

function TelegramRow({ reward }: { reward: SocialRewardController }) {
  const { t } = useTranslation();
  const { telegram } = reward.statusQuery.data!;
  const linksQuery = reward.telegramLinksQuery;
  const links = linksQuery.data;
  // On phones https://t.me opens the app itself; desktop browsers need tg:// for Telegram Desktop.
  const mobile = getInstallPlatform() !== 'desktop';
  const notMember = telegram.connected && telegram.subscribed === false;
  const watchTelegram = () => reward.watch('telegram');
  const status = notMember
    ? 'socialReward.notMember'
    : reward.opened.telegram
      ? 'socialReward.telegramWaiting'
      : linksQuery.isError
        ? 'socialReward.linksError'
        : 'socialReward.telegramStatus';

  const action = links ? (
    <LinkAction className="sr-action" href={mobile ? links.inviteUrl : links.inviteAppUrl} onClick={watchTelegram}>
      {t('socialReward.subscribe')}
    </LinkAction>
  ) : linksQuery.isError ? (
    // Without a personal link the join cannot confirm itself; the bot still can.
    <LinkAction className="sr-action" href={telegram.url}>
      {t('socialReward.subscribe')}
    </LinkAction>
  ) : (
    <span className="sr-action sr-action--pending" aria-busy="true">
      {t('socialReward.subscribe')}
    </span>
  );

  let extra: ReactNode = null;
  if (notMember) {
    extra = (
      <button type="button" className="sr-mini" disabled={reward.busy} onClick={reward.check}>
        {t(reward.checking ? 'socialReward.checking' : 'socialReward.checkAgain')}
      </button>
    );
  } else if (linksQuery.isError) {
    extra = (
      <button
        type="button"
        className="sr-mini"
        disabled={linksQuery.isFetching}
        onClick={() => void linksQuery.refetch()}
      >
        {t('socialReward.retry')}
      </button>
    );
  } else if (links) {
    extra = (
      <>
        <LinkAction className="sr-mini" href={mobile ? links.botUrl : links.botAppUrl} onClick={watchTelegram}>
          {t('socialReward.telegramBot')}
        </LinkAction>
        {!mobile && (
          <LinkAction className="sr-mini" href={links.inviteUrl} onClick={watchTelegram}>
            {t('socialReward.openInBrowser')}
          </LinkAction>
        )}
      </>
    );
  }

  return (
    <NetworkRow
      network="telegram"
      verified={telegram.subscribed === true}
      status={t(status)}
      action={action}
      extra={extra}
    />
  );
}

function VkRow({ reward }: { reward: SocialRewardController }) {
  const { t } = useTranslation();
  const { vk } = reward.statusQuery.data!;
  const linkQuery = reward.vkLinkQuery;
  const notMember = vk.connected && vk.subscribed === false;
  // The chat link names the account for the community bot. A linked account is
  // confirmed by the join event itself, so the community page is enough.
  const href = notMember ? vk.url : (linkQuery.data ?? (linkQuery.isError ? vk.url : null));
  const onOpen = () => {
    if (notMember) reward.recheckVkOnReturn();
    reward.watch('vk');
  };
  const status = notMember
    ? 'socialReward.notMember'
    : reward.opened.vk
      ? 'socialReward.vkWaiting'
      : linkQuery.isError
        ? 'socialReward.linksError'
        : vk.connected
          ? 'socialReward.vkLinked'
          : 'socialReward.vkStatus';

  let extra: ReactNode = null;
  if (vk.connected) {
    extra = (
      <button type="button" className="sr-mini" disabled={reward.busy} onClick={reward.check}>
        {t(reward.checking ? 'socialReward.checking' : notMember ? 'socialReward.checkAgain' : 'socialReward.check')}
      </button>
    );
  } else if (linkQuery.isError) {
    extra = (
      <button
        type="button"
        className="sr-mini"
        disabled={linkQuery.isFetching}
        onClick={() => void linkQuery.refetch()}
      >
        {t('socialReward.retry')}
      </button>
    );
  }

  return (
    <NetworkRow
      network="vk"
      verified={vk.subscribed === true}
      status={t(status)}
      action={
        href ? (
          <LinkAction className="sr-action" href={href} onClick={onOpen}>
            {t('socialReward.subscribe')}
          </LinkAction>
        ) : (
          <span className="sr-action sr-action--pending" aria-busy="true">
            {t('socialReward.subscribe')}
          </span>
        )
      }
      extra={extra}
    />
  );
}

/** The network list stays visible in every state: one subscription earns the gift, the rest are still welcome. */
function Networks({ reward }: { reward: SocialRewardController }) {
  const { t } = useTranslation();
  const data = reward.statusQuery.data!;
  const locked = data.rewardStatus === 'locked';
  return (
    <>
      <p className={locked ? 'sr-intro' : 'sr-label'}>{t(locked ? 'socialReward.dialogIntro' : 'socialReward.more')}</p>
      <div className="sr-choice">
        <TelegramRow reward={reward} />
        <div className={cn('sr-or', !locked && 'sr-or--plain')} aria-hidden="true">
          {locked && <span>{t('socialReward.or')}</span>}
        </div>
        <VkRow reward={reward} />
      </div>
      <NetworkRow
        network="dzen"
        quiet
        status={t('socialReward.dzenStatus')}
        action={
          <LinkAction className="sr-action sr-action--ghost" href={data.dzenUrl}>
            {t('socialReward.dzenOpen')}
          </LinkAction>
        }
      />
    </>
  );
}

/** Earned, not used yet: one line on top of the list with its own button. */
function RewardBox({ reward }: { reward: SocialRewardController }) {
  const { t } = useTranslation();
  const data = reward.statusQuery.data!;
  const quota = reward.quotaQuery.data;
  const reason = data.reason === 'no_usage' ? 'socialReward.noUsageShort' : null;
  // The server also allows completing an interrupted reset whose quota is
  // already zero. Quota is descriptive; reward status owns eligibility.
  const canUse = data.canUse && !reason;
  return (
    <div className="sr-reward" role="status">
      <span className="sr-reward__mark">
        <CheckIcon aria-hidden="true" />
      </span>
      <div className="sr-row__body">
        <h3>{t('socialReward.card.readyTitle')}</h3>
        <p className="sr-row__status">
          {reason
            ? t(reason)
            : quota
              ? t('socialReward.usage', { used: quota.usedToday, limit: quota.dailyLimit })
              : t('socialReward.card.readyHint')}
        </p>
      </div>
      <button
        type="button"
        className="sr-action"
        disabled={!canUse || reward.busy || reward.statusQuery.isFetching}
        onClick={reward.confirm}
      >
        {t('socialReward.card.use')}
      </button>
    </div>
  );
}

function UsedBox({ reward }: { reward: SocialRewardController }) {
  const { t } = useTranslation();
  return (
    <div className="sr-reward sr-reward--used" role="status">
      <span className="sr-reward__mark">
        <CheckIcon aria-hidden="true" />
      </span>
      <div className="sr-row__body">
        <h3>{t(reward.redeemed ? 'socialReward.redeemedTitle' : 'socialReward.card.usedTitle')}</h3>
        <p className="sr-row__status">
          {t(reward.redeemed ? 'socialReward.redeemedHint' : 'socialReward.card.usedHint')}
        </p>
      </div>
    </div>
  );
}

function ConfirmReset({ reward }: { reward: SocialRewardController }) {
  const { t } = useTranslation();
  const data = reward.statusQuery.data!;
  const quota = reward.quotaQuery.data;
  const canUse = data.canUse && data.reason !== 'no_usage';
  return (
    <>
      <p className="sr-intro">{t('socialReward.confirmDescription')}</p>
      {quota && (
        <p className="sr-usage">{t('socialReward.usage', { used: quota.usedToday, limit: quota.dailyLimit })}</p>
      )}
      {reward.quotaQuery.isError && (
        <div role="alert" className="sr-message">
          <p>{t('socialReward.quotaError')}</p>
          <Button
            variant="naked"
            size="sm"
            disabled={reward.quotaQuery.isFetching}
            onClick={() => void reward.quotaQuery.refetch()}
          >
            {t('socialReward.retry')}
          </Button>
        </div>
      )}
      <div className="sr-dialog-actions">
        <p className="sr-footnote">{t('socialReward.confirmOnce')}</p>
        <Button fullWidth disabled={!canUse || reward.busy || reward.statusQuery.isFetching} onClick={reward.useReset}>
          {t(reward.using ? 'socialReward.redeeming' : 'socialReward.confirmUse')}
        </Button>
        <Button variant="ghost" fullWidth disabled={reward.using} onClick={reward.cancelConfirm}>
          {t('socialReward.cancel')}
        </Button>
      </div>
    </>
  );
}

export function SocialRewardDialog({ reward }: { reward: SocialRewardController }) {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const previousStageRef = useRef('');
  const { data, isPending, isError, isFetching, refetch } = reward.statusQuery;
  const available = data?.enabled && data.rewardStatus === 'available';
  const used = data?.enabled && data.rewardStatus === 'used';
  const title = used
    ? reward.redeemed
      ? 'socialReward.redeemedTitle'
      : 'socialReward.usedTitle'
    : available
      ? reward.confirming
        ? 'socialReward.confirmTitle'
        : 'socialReward.readyTitle'
      : 'socialReward.title';
  const stage = `${isPending ? 'loading' : (data?.rewardStatus ?? 'unavailable')}:${reward.confirming}`;

  useEffect(() => {
    // The active button may disappear after verification or redemption.
    // Keep keyboard focus inside the existing modal as its content changes.
    if (reward.open && previousStageRef.current && previousStageRef.current !== stage) {
      contentRef.current?.focus();
    }
    previousStageRef.current = reward.open ? stage : '';
  }, [reward.open, stage]);

  return (
    <Modal
      open={reward.open}
      onClose={reward.close}
      title={t(title)}
      kicker={t('socialReward.kicker')}
      width={480}
      className="sr-dialog"
    >
      <div ref={contentRef} tabIndex={-1} aria-label={t(title)} className="sr-dialog-content">
        {isPending ? (
          <p role="status" className="sr-intro">
            {t('socialReward.loading')}
          </p>
        ) : !data || !data.enabled || data.rewardStatus === 'unavailable' ? (
          <div role="status" className="sr-dialog-actions">
            <p className="sr-intro">{t(isError ? 'socialReward.loadError' : 'socialReward.unavailableCard')}</p>
            <Button variant="ghost" disabled={isFetching} onClick={() => void refetch()}>
              {t('socialReward.retry')}
            </Button>
          </div>
        ) : available && reward.confirming ? (
          <ConfirmReset reward={reward} />
        ) : (
          <>
            {available && <RewardBox reward={reward} />}
            {used && <UsedBox reward={reward} />}
            <Networks reward={reward} />
          </>
        )}
        {isError && data?.enabled && (
          <div role="alert" className="sr-message">
            <p>{t('socialReward.loadError')}</p>
            <Button variant="naked" size="sm" disabled={isFetching} onClick={() => void refetch()}>
              {t('socialReward.retry')}
            </Button>
          </div>
        )}
        {reward.feedback && (
          <p
            role={reward.feedback.tone === 'error' ? 'alert' : 'status'}
            className={cn('sr-message', reward.feedback.tone === 'error' && 'sr-message--error')}
          >
            {t(reward.feedback.key)}
          </p>
        )}
      </div>
    </Modal>
  );
}
